import React, { useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { closeDialog } from '@shared/store/dialog.duck';
import { selectHideFavouriteControls, selectOfficeForReservation } from '@features/desks/store/deskReservation.duck';
import { MobileOnly } from "@shared/ui/atoms/DeviceSpecific";
import { GoBackButton } from "@shared/ui/atoms/buttons/GoBackButton";
import DialogHeader from "@shared/dialogs/DialogHeader";
import { FlatContentCard } from "@shared/ui/atoms/FlatContentCard";
import { BodyRegular } from "@shared/ui/atoms/fonts/Body";
import { LinkButton } from "@shared/ui/atoms/buttons/LinkButton";
import OfficeLocationContentCard from "@shared/ui/molecules/OfficeLocationContentCard";
import styled from "styled-components";
import { Center, Row } from "@shared/ui/atoms/StructuralLayout";
import { selectActiveOffices } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { warningNotification } from '@shared/store/notification.duck';
import { useTranslation } from "react-i18next";
import { WithSeatsAvailableInAllOffices } from '@features/desks/hooks/WithSeatsAvailableInAllOffices';
import { OfficeWithCapacity } from '@features/desks/services/OfficeCapacityService';
import { Moment } from "moment";
import { LoadingSpinner } from "@shared/ui/atoms/LoadingSpinner";

interface Props {
  activeDate: Moment;
}

export function ChooseOffice(props: Props) {
  const dispatch = useDispatch();
  const offices = useSelector(selectActiveOffices);
  const close = () => dispatch(closeDialog());
  const hideFavouriteButton = useSelector(selectHideFavouriteControls);
  const {t} = useTranslation();

  const {allCapacities, isLoading} = WithSeatsAvailableInAllOffices(props.activeDate);

  const allOfficesWithCapacities = useMemo(() => {
    return offices.map((office: OfficeEntity) => {
      const capacity = allCapacities.find(c => c.officeId === office.id);
      if (capacity) {
        return {
          office: office,
          capacity: capacity
        }
      } else {
        return undefined;
      }
    }).filter((owc?: OfficeWithCapacity) => !!owc);
  }, [offices, allCapacities]);

  const officeSelected = (office: OfficeEntity) => {
    if (office.active) {
      dispatch(selectOfficeForReservation(office));
    } else {
      dispatch(warningNotification('This office is not active.'));
    }
  };

  return (
    <>
      <MobileOnly>
        <GoBackButton onClick={close} text="back-to-whereabouts"/>
      </MobileOnly>
      <DialogHeader title="dialog-headers.choose-office"/>

      {offices.length === 0 && <FlatContentCard>
        <BodyRegular weight={600}>{t('whereabouts.no-offices')}</BodyRegular>
        <BodyRegular>{t('whereabouts.no-offices-description')}</BodyRegular>
        <LinkButton text="whereabouts.add-office-button" link="/manage/advance-hot-desking-setup"/>
      </FlatContentCard>}

      {isLoading ? <>
        <Center>
          <LoadingSpinner hideBorder={true} />
        </Center>
      </> : <>
        <WrappedRow>
          {allOfficesWithCapacities.map((owc: OfficeWithCapacity, key: number) => (
            <OfficeLocationContentCard onClick={() => officeSelected(owc.office)}
                                       key={key} hideFavourite={!!hideFavouriteButton}
                                       capacity={owc.capacity}
                                       office={owc.office}/>
          ))}
        </WrappedRow>
      </>}


      <FlatContentCard>
        <BodyRegular weight={600}>{t('whereabouts.favouriting-suggestion-title')}</BodyRegular>
        <BodyRegular>{t('whereabouts.favouriting-suggestion-description')}</BodyRegular>
      </FlatContentCard>
    </>
  )
}

const WrappedRow = styled(Row)`
  flex-wrap: wrap;
  margin-bottom: 24px;
`
